import { useAderenciaPrecosStore } from '../AderenciaPrecos.store';
import { useDebounce } from '../../../../../hooks';
import * as service from '../services';
import { ADERENCIA_BODY_REQUEST_NAMES, FILTER_NAMES } from '../data';
import { FilterNames, FiltersData } from '../types';
import { useEffect } from 'react';

interface FilterItem {
  value: string;
  label: string;
}

export const useAderenciaFilters = () => {
  const { setAderenciaPrecosState, setAderenciaBodyRequests, rede, uf, tipoLoja, segmento } = useAderenciaPrecosStore();

  const debounce = useDebounce();

  const FILTERS: Record<FilterNames, typeof rede> = {
    REDE: rede,
    TIPO_LOJA: tipoLoja,
    UF: uf,
    SEGMENTO: segmento,
  };

  const getFilterState = (name: FilterNames) => FILTERS[name];

  const updateFilterState = (name: FilterNames, data: FiltersData) => {
    const prev = getFilterState(name);
    setAderenciaPrecosState(FILTER_NAMES[name], { ...prev, ...data });
  };

  const setList = (name: FilterNames, data: string[]) => {
    const list = data?.map(item => ({
      value: item,
      label: item,
    }));

    const prev = getFilterState(name);
    const cacheList = prev.cache;
    const removeDuplicate = list.filter(i => !prev.value?.includes(i.value));
    const concatList = prev.value?.length ? cacheList?.concat(removeDuplicate) : list;
    setAderenciaPrecosState(FILTER_NAMES[name], { ...prev, list: concatList });
  };

  const getAderenciaFiltros = async (name: FilterNames, query: string) => {
    const state = useAderenciaPrecosStore.getState();

    const bodyRequest = name === 'REDE' ? { campo: name, query: query, segmentosPersonalizados: state.segmento.value } : { campo: name, query: query };
    const serviceMethod = name === 'REDE' ? service.getAderenciaPrecosDetailingFiltros : service.getAderenciaPrecosFiltros;

    try {
      const response = await serviceMethod(bodyRequest);

      if (response?.status === 200) {
        setList(name, response.data?.content ?? []);
      }
    } catch (error) {
      console.error(`Erro ao buscar filtros para ${name}:`, error);
    }
  };

  const handleAderenciaFitersSearch = (name: FilterNames, query: string) => {
    debounce(() => getAderenciaFiltros(name, [query]));
  };

  /** Adiciona item ao filtro */
  const addFilterItem = (name: FilterNames, item: FilterItem) => {
    const prev = getFilterState(name);

    if (!prev.value?.includes(item.value)) {
      const value = [...(prev.value || []), item.value];
      const cache = [...(prev.cache || []), item];

      updateFilterState(name, {
        value,
        cache,
      });

      setAderenciaBodyRequests(ADERENCIA_BODY_REQUEST_NAMES[name], value);
    }
  };

  /** Remove item do filtro */
  const removeFilterItem = (name: FilterNames, item: FilterItem) => {
    const prev = getFilterState(name);
    const value = (prev.value || []).filter(value => value !== item.value);
    const cache = (prev.cache || []).filter(cache => cache.value !== item.value);

    updateFilterState(name, {
      value,
      cache,
    });

    setAderenciaBodyRequests(ADERENCIA_BODY_REQUEST_NAMES[name], value);
  };

  /** Alterna seleção do item */
  const handleSelectAderenciaFilter = (name: FilterNames, item: FilterItem) => {
    const prev = getFilterState(name);
    if (prev.value?.includes(item.value)) {
      removeFilterItem(name, item);
    } else {
      addFilterItem(name, item);
    }
  };

  /** Limpa o filtro */
  const handleCleanAderenciaFilter = (name: FilterNames) => {
    updateFilterState(name, { value: [], cache: [] });
    setAderenciaBodyRequests(ADERENCIA_BODY_REQUEST_NAMES[name], []);
  };

  useEffect(() => {
    handleAderenciaFitersSearch('SEGMENTO', '');
  }, []);

  return {
    handleAderenciaFitersSearch,
    handleCleanAderenciaFilter,
    handleSelectAderenciaFilter,
  };
};
